import type { ReactNode } from 'react';
import { AdminSpinner } from './AdminSpinner';
import { AiOutlineReload, AiOutlineSafetyCertificate } from 'react-icons/ai';

import logo from '../static/images/logo.png';

interface AdminStateScreenProps {
  title: string;
  description?: string;
  tone?: 'error' | 'loading' | 'neutral';
  detailLabel?: string;
  technicalDetails?: readonly string[];
  actionLabel?: string;
  onAction?: () => void;
}

export function AdminStateScreen({
  title,
  description,
  tone = 'neutral',
  detailLabel,
  technicalDetails,
  actionLabel,
  onAction,
}: AdminStateScreenProps) {
  const isError = tone === 'error';
  const isLoading = tone === 'loading';

  return (
    <main className="admin-state-shell">
      <section
        className={`admin-state-panel admin-state-panel--${tone}`}
        role={isError ? 'alert' : 'status'}
        aria-live={isError ? 'assertive' : 'polite'}
        aria-busy={isLoading || undefined}
      >
        <img className="admin-state-logo" src={logo} alt="Tích Chỉ" />
        {isLoading ? (
          <div className="admin-state-progress" aria-hidden="true">
            <AdminSpinner size="large" />
          </div>
        ) : null}
        <div className="admin-state-copy">
          <h1>{title}</h1>
          {description ? <p>{description}</p> : null}
        </div>
        {technicalDetails?.length && detailLabel ? (
          <details className="admin-state-details">
            <summary>{detailLabel}</summary>
            <ul>
              {technicalDetails.map((detail) => (
                <li key={detail}>
                  <code>{detail}</code>
                </li>
              ))}
            </ul>
          </details>
        ) : null}
        {actionLabel && onAction ? (
          <button
            className="admin-action-button"
            type="button"
            onClick={onAction}
          >
            <AiOutlineReload aria-hidden="true" />
            {actionLabel}
          </button>
        ) : null}
      </section>
    </main>
  );
}

interface AdminContextBarProps {
  contextLabel: string;
  environmentLabel: string;
  environmentValue: string;
  projectLabel: string;
  projectValue: string;
  authorityLabel: string;
  authorityValue: string;
}

function ContextItem({
  icon,
  label,
  value,
  modifier,
  accessibleLabel,
}: {
  icon?: ReactNode;
  label: string;
  value: string;
  modifier?: string;
  accessibleLabel?: string;
}) {
  return (
    <span
      className={`admin-context-item ${modifier ?? ''}`.trim()}
      aria-label={accessibleLabel}
      title={accessibleLabel}
    >
      {icon}
      <span className="admin-context-label">{label}</span>
      <strong>{value}</strong>
    </span>
  );
}

export function AdminContextBar({
  contextLabel,
  environmentLabel,
  environmentValue,
  projectLabel,
  projectValue,
  authorityLabel,
  authorityValue,
}: AdminContextBarProps) {
  return (
    <div className="admin-context-bar" aria-label={contextLabel} role="status">
      <ContextItem
        label={environmentLabel}
        value={`${environmentValue} · ${projectValue}`}
        modifier="admin-context-item--project"
        accessibleLabel={`${environmentLabel}: ${environmentValue}. ${projectLabel}: ${projectValue}.`}
      />
      <ContextItem
        icon={<AiOutlineSafetyCertificate aria-hidden="true" />}
        label={authorityLabel}
        value={authorityValue}
        modifier="admin-context-item--authority"
      />
    </div>
  );
}
